import { useCallback } from "react";
import useSWR from "swr";
import { FileInfo, Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { CarPurchaseDocument, RenameCarPurchaseDocument } from "./types";

export const useCarPurchaseDocumentsApi = () => {
  const api = useApi();

  const useCarPurchaseDocuments = useCallback(
    (query: PaginationQuery, signal: AbortSignal, carPurchaseId?: string) => {
      const url = buildUrl(
        carPurchaseId ? `/admin/car_purchases/${carPurchaseId}/documents` : "/admin/car_purchase_documents",
        query,
      );
      return useSWR<Paginated<CarPurchaseDocument>, CustomError, string>(url, (url) =>
        api.get<Paginated<CarPurchaseDocument>>({ url, signal }),
      );
    },
    [api],
  );

  const deleteCarPurchaseDocument = useCallback(
    async (documentId: string, carPurchaseId?: string): Promise<void> => {
      const url = `/admin/car_purchase_documents/${documentId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/car_purchase_documents`, `/admin/car_purchases/${carPurchaseId}/documents`]);
      });
    },
    [api],
  );

  const insertCarPurchaseDocument = useCallback(
    async (file: File, overWrite?: boolean, carPurchaseId?: string): Promise<void> => {
      const formData = new FormData();
      formData.append("file", file, file.name);

      await api.postNoResult<FormData>(
        `/admin/car_purchases/${carPurchaseId}/documents${overWrite ? "?overwrite=1" : ""}`,
        formData,
      );
      api.mutateCache([`/admin/car_purchases/${carPurchaseId}/documents`, "/admin/car_purchase_documents"]);
    },
    [api],
  );

  const getCarPurchaseDocument = useCallback(
    (docID: string, signal: AbortSignal) => {
      const url = `/admin/car_purchase_documents/${docID}`;
      const r = api.get<FileInfo>({ url, signal });
      return r;
    },
    [api],
  );

  const renameDocument = useCallback(
    async (docId: string, body: RenameCarPurchaseDocument, carPurchaseId?: string): Promise<void> => {
      await api.put<RenameCarPurchaseDocument>(`/admin/car_purchase_documents/${docId}`, body);
      api.mutateCache([`/admin/car_purchases/${carPurchaseId}/documents`, "/admin/car_purchase_documents"]);
    },
    [api],
  );

  return {
    useCarPurchaseDocuments,
    deleteCarPurchaseDocument,
    insertCarPurchaseDocument,
    getCarPurchaseDocument,
    renameDocument,
  };
};
